import React, { useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material';
import {
  Home as HomeIcon,
  Replay as ReplayIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Timer as TimerIcon,
  EmojiEvents as TrophyIcon,
  ExpandMore as ExpandMoreIcon,
  Star as StarIcon,
  AccessTime as AccessTimeIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { useQuizHistory } from '../../../hooks/useQuizHistory';
import { useAuth } from '../../../hooks/useAuth';
import { useLiveQuizSessions } from '../../../hooks/useLiveQuiz';
import { styled } from '@mui/material/styles';

// Styled components for enhanced UI
const QuestionCard = styled(Card)(({ theme, isCorrect }) => ({
  marginBottom: theme.spacing(2),
  border: `2px solid ${isCorrect ? theme.palette.success.main : theme.palette.error.main}`,
  borderRadius: '12px',
  backgroundColor: isCorrect ? 'rgba(76, 175, 80, 0.05)' : 'rgba(244, 67, 54, 0.05)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const AnswerOption = styled(Box)(({ theme, isCorrect, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  margin: theme.spacing(0.5, 0),
  borderRadius: '8px',
  backgroundColor: isCorrect
    ? 'rgba(76, 175, 80, 0.1)'
    : isSelected
      ? 'rgba(244, 67, 54, 0.1)'
      : 'rgba(0, 0, 0, 0.05)',
  border: `1px solid ${isCorrect ? theme.palette.success.main : isSelected ? theme.palette.error.main : theme.palette.divider}`,
  transition: 'all 0.2s ease',
}));

const StatCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  borderRadius: '12px',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
}));

const QuizResults = ({ config, quizData, results, onReplay, onGoHome }) => {
  const { saveQuizResult } = useQuizHistory();
  const { user, isAuthenticated, token } = useAuth();
  const hasSavedRef = useRef(false);
  const hasSavedToBackendRef = useRef(false);
  const hasCompletedLiveRef = useRef(false);
  const { completeSession } = useLiveQuizSessions(false);

  // Save quiz history to backend for registered users
  const saveQuizHistoryToBackend = useCallback(async () => {
    try {
      if (!token) {
        console.error('No authentication token available for saving quiz history');
        return;
      }
      const sessionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const requestBody = {
        quiz_config: config,
        quiz_data: quizData,
        results: results,
        total_score: results?.total_score || results?.totalScore || 0,
        completion_time: results?.completion_time || 0,
        session_id: sessionId
      };
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/quiz-history/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      const responseData = await response.json();
      if (!response.ok) {
        console.error('Failed to save quiz history to backend:', responseData);
      }
    } catch (error) {
      console.error('Error saving quiz history to backend:', error);
    }
  }, [token, config, quizData, results]);

  // Save results to history when component mounts
  useEffect(() => {
    // console.log('QuizResults useEffect triggered');
    // console.log('Results:', !!results);
    // console.log('Has saved:', hasSavedRef.current);
    // console.log('Is authenticated:', isAuthenticated);
    // console.log('User type:', user?.user_type);
    
    if (results) {
      // console.log('Saving quiz results...');
      
      // Always save to localStorage for local history (only once)
      if (!hasSavedRef.current) {
        saveQuizResult(results);
        hasSavedRef.current = true; // Mark as saved to prevent duplicates
      }
      
      // Also save to backend for registered users (retry if auth becomes available)
      if (isAuthenticated && user?.user_type === 'registered' && !hasSavedToBackendRef.current) {
        // console.log('User is registered, saving to backend...');
        hasSavedToBackendRef.current = true; // Mark as saved to prevent duplicates
        saveQuizHistoryToBackend();
      } else {
        // console.log('User is not registered or not authenticated, skipping backend save');
        // console.log('isAuthenticated:', isAuthenticated);
        // console.log('user?.user_type:', user?.user_type);
        // console.log('hasSavedToBackend:', hasSavedToBackendRef.current);
      }
    }
  }, [results, isAuthenticated, user, saveQuizResult, saveQuizHistoryToBackend]);

  // If this is a live quiz, send completion to backend once (fallback to localStorage when URL param is missing)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      let sessionId = params.get('sessionId');
      let isLive = params.get('live') === 'true' || !!sessionId;
      if (!sessionId) {
        // Fallback: use stored session id from localStorage
        const storedSessionId = localStorage.getItem('live_session_id');
        if (storedSessionId) {
          sessionId = storedSessionId;
          isLive = true;
        }
      }
      if (!isLive || !results || hasCompletedLiveRef.current) return;

      // Compute final stats
      const allQuestionResults = results.question_results ||
        Object.values(results.quizlet_results || {}).flatMap(qr => qr.questionResults || qr.question_results || []);
      const totalQuestions = allQuestionResults.length || (results.total_questions || 0);
      const correctAnswers = (allQuestionResults.filter(q => q?.isCorrect).length) || (results.correct_answers || 0);
      const finalScore = results?.total_score || results?.totalScore || 0;
      const percentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
      const timeTakenSeconds = results?.completion_time || 0; // show only total time taken

      // Identity
      const deviceId = localStorage.getItem('device_id');
      const userId = isAuthenticated && user ? user.id : null;

      if (!sessionId || !deviceId) return;

      hasCompletedLiveRef.current = true;
      completeSession(sessionId, {
        device_id: deviceId,
        user_id: userId,
        final_score: finalScore,
        total_questions: totalQuestions,
        correct_answers: correctAnswers,
        percentage,
        time_taken_seconds: timeTakenSeconds
      }).then(() => {
        // Clear live session context upon successful completion
        try {
          localStorage.removeItem('live_session_id');
          localStorage.removeItem('live_participant_id');
        } catch (_) {}
      }).catch(() => {
        // swallow errors to avoid interrupting results UI
        hasCompletedLiveRef.current = true;
      });
    } catch (e) {
      // ignore
    }
  }, [results, isAuthenticated, user, completeSession]);


  const getPerformanceStats = () => {
    // // console.log('[QuizResults] getPerformanceStats called');
    // // console.log('[QuizResults] results:', results);
    // // console.log('[QuizResults] results?.question_results:', results?.question_results);
    // // console.log('[QuizResults] results?.quizlet_results:', results?.quizlet_results);
    
    if (!results?.question_results && !results?.quizlet_results) {
      // // console.log('[QuizResults] No question_results or quizlet_results found, returning null');
      return null;
    }

    // Handle both individual quizlet results and combined results
    const allQuestionResults = results.question_results ||
      Object.values(results.quizlet_results || {}).flatMap(qr => qr.questionResults || qr.question_results || []);
    
    // // console.log('[QuizResults] allQuestionResults:', allQuestionResults);
    // // console.log('[QuizResults] allQuestionResults length:', allQuestionResults.length);

    const totalQuestions = allQuestionResults.length;
    const correctAnswers = allQuestionResults.filter(q => q?.isCorrect).length;
    const accuracy = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
    
    // // console.log('[QuizResults] totalQuestions:', totalQuestions);
    // // console.log('[QuizResults] correctAnswers:', correctAnswers);
    // // console.log('[QuizResults] accuracy:', accuracy);
    
    // If no question results found, try to get stats from quizlet results
    if (totalQuestions === 0 && results?.quizlet_results) {
      // // console.log('[QuizResults] No question results, calculating from quizlet results');
      const quizletStats = Object.values(results.quizlet_results);
      const totalQuestionsFromQuizlets = quizletStats.reduce((sum, qr) => sum + (qr.totalQuestions || 0), 0);
      const totalCorrectFromQuizlets = quizletStats.reduce((sum, qr) => sum + (qr.correctAnswers || 0), 0);
      const accuracyFromQuizlets = totalQuestionsFromQuizlets > 0 ? Math.round((totalCorrectFromQuizlets / totalQuestionsFromQuizlets) * 100) : 0;
      
      // // console.log('[QuizResults] Stats from quizlets - totalQuestions:', totalQuestionsFromQuizlets, 'correctAnswers:', totalCorrectFromQuizlets, 'accuracy:', accuracyFromQuizlets);
      
      return {
        totalQuestions: totalQuestionsFromQuizlets,
        correctAnswers: totalCorrectFromQuizlets,
        accuracy: accuracyFromQuizlets,
        averageTime: 0,
        bestQuestion: { scoreDelta: 0 },
        worstQuestion: { scoreDelta: 0 },
        quizletStats: Object.entries(results.quizlet_results).map(([index, qr]) => ({
          quizletIndex: parseInt(index),
          score: qr?.total_score || qr?.totalScore || 0,
          correctAnswers: qr?.correctAnswers || 0,
          questionsAnswered: qr?.questionsAnswered || qr?.totalQuestions || 0,
          accuracy: (qr?.questionsAnswered || qr?.totalQuestions || 0) > 0 ?
            Math.round(((qr?.correctAnswers || 0) / (qr?.questionsAnswered || qr?.totalQuestions || 1)) * 100) : 0,
          completionTime: qr?.completion_time || qr?.timeTaken || 0,
          timedOut: qr?.timedOut || false
        }))
      };
    }

    const averageTime = totalQuestions > 0
      ? Math.round(allQuestionResults.reduce((sum, q) => sum + (q?.timeTaken || 0), 0) / totalQuestions)
      : 0;

    const bestQuestion = allQuestionResults.length > 0 ? allQuestionResults.reduce((best, current) =>
      (current?.scoreDelta || 0) > (best?.scoreDelta || 0) ? current : best,
      allQuestionResults[0] || { scoreDelta: 0 }
    ) : { scoreDelta: 0 };

    const worstQuestion = allQuestionResults.length > 0 ? allQuestionResults.reduce((worst, current) =>
      (current?.scoreDelta || 0) < (worst?.scoreDelta || 0) ? current : worst,
      allQuestionResults[0] || { scoreDelta: 0 }
    ) : { scoreDelta: 0 };

    // Calculate quizlet-specific stats
    const quizletStats = results?.quizlet_results ?
      Object.entries(results.quizlet_results).map(([index, qr]) => ({
        quizletIndex: parseInt(index),
        score: qr?.total_score || qr?.totalScore || 0,
        correctAnswers: qr?.correctAnswers || 0,
        questionsAnswered: qr?.questionsAnswered || qr?.totalQuestions || 0,
        accuracy: (qr?.questionsAnswered || qr?.totalQuestions || 0) > 0 ?
          Math.round(((qr?.correctAnswers || 0) / (qr?.questionsAnswered || qr?.totalQuestions || 1)) * 100) : 0,
        completionTime: qr?.completion_time || qr?.timeTaken || 0,
        timedOut: qr?.timedOut || false
      })) : [];

    return {
      totalQuestions,
      correctAnswers,
      accuracy,
      averageTime,
      bestQuestion,
      worstQuestion,
      quizletStats
    };
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatScore = (score) => {
    if (score === null || score === undefined) return '0';
    if (score > 0) return `+${score.toLocaleString()}`;
    if (score < 0) return score.toLocaleString();
    return '0';
  };

  // Sharing UI removed; function deleted because it wasn't used

  const stats = getPerformanceStats();
  
  // // console.log('[QuizResults] stats:', stats);

  // If no stats available, show a message
  if (!stats) {
    return (
      <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, textAlign: 'center' }}>
        <Typography variant="h4" color="error" gutterBottom>
          No Performance Data Available
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Unable to load quiz performance statistics. The quiz may not have been completed properly.
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={4}>
          Results data: {results ? 'Available' : 'Not available'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ReplayIcon />}
          onClick={onReplay}
          size="large"
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
      {/* Header */}
      <Card sx={{ mb: 4, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <TrophyIcon sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h3" component="h1" gutterBottom>
            Quiz Complete!
          </Typography>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Final Score: {(results?.total_score || results?.totalScore || 0).toLocaleString()}
          </Typography>
          <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
            <Chip
              label={`${quizData?.difficulty?.level_name} Difficulty`}
              variant="outlined"
              sx={{ color: 'inherit', borderColor: 'inherit' }}
            />
            <Chip
              label={`${stats?.accuracy}% Accuracy`}
              variant="outlined"
              sx={{ color: 'inherit', borderColor: 'inherit' }}
            />
            <Chip
              label={`${formatTime(results?.completion_time || 0)} Duration`}
              variant="outlined"
              sx={{ color: 'inherit', borderColor: 'inherit' }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Enhanced Performance Summary */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                <CheckIcon sx={{ fontSize: 32, color: 'success.main', mr: 1 }} />
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {stats.correctAnswers}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight="500">
                Correct Answers
              </Typography>
              <Typography variant="caption" color="text.secondary">
                out of {stats.totalQuestions} questions
              </Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                <StarIcon sx={{ fontSize: 32, color: 'warning.main', mr: 1 }} />
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {stats.accuracy}%
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight="500">
                Accuracy Rate
              </Typography>
              <LinearProgress
                variant="determinate"
                value={stats.accuracy}
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
                color={stats.accuracy >= 80 ? 'success' : stats.accuracy >= 60 ? 'warning' : 'error'}
              />
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                <AccessTimeIcon sx={{ fontSize: 32, color: 'info.main', mr: 1 }} />
                <Typography variant="h4" color="info.main" fontWeight="bold">
                  {stats.averageTime}s
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight="500">
                Average Time
              </Typography>
              <Typography variant="caption" color="text.secondary">
                per question
              </Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                <TrophyIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1 }} />
                <Typography variant="h4" color="primary.main" fontWeight="bold">
                  {formatScore(stats?.bestQuestion?.scoreDelta || 0)}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" fontWeight="500">
                Best Question
              </Typography>
              <Typography variant="caption" color="text.secondary">
                highest score
              </Typography>
            </StatCard>
          </Grid>
        </Grid>
      )}

     
      {stats?.quizletStats && stats.quizletStats.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quizlet Performance Breakdown
            </Typography>
            <Grid container spacing={2}>
              {stats.quizletStats.map((quizletStat) => {
                const song = quizData?.quiz_data?.[quizletStat.quizletIndex]?.song;
                return (
                  <Grid item xs={12} sm={6} md={4} key={quizletStat.quizletIndex}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                          <Avatar
                            src={song?.image}
                            sx={{ width: 40, height: 40, mr: 2 }}
                          />
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Quizlet {quizletStat.quizletIndex + 1}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {song?.name}
                            </Typography>
                          </Box>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">Score:</Typography>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {formatScore(quizletStat?.score || 0)}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">Accuracy:</Typography>
                          <Typography variant="body2" fontWeight="bold" color="secondary">
                            {quizletStat.accuracy}%
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">Correct:</Typography>
                          <Typography variant="body2">
                            {quizletStat.correctAnswers}/{quizletStat.questionsAnswered}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2">Time:</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatTime(quizletStat.completionTime)}
                          </Typography>
                        </Box>
                        {quizletStat.timedOut && (
                          <Chip
                            label="Timed Out"
                            size="small"
                            color="warning"
                            icon={<TimerIcon />}
                            sx={{ mt: 1 }}
                          />
                        )}
                        {results.mainTimerExpired && (
                          <Chip
                            label="Main Timer Expired"
                            size="small"
                            color="error"
                            icon={<TimerIcon />}
                            sx={{ mt: 1 }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Enhanced Question-by-Question Breakdown */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3}>
            <PsychologyIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" fontWeight="bold">
              Detailed Question Analysis
            </Typography>
          </Box>


          {(results.question_results ||
            Object.values(results.quizlet_results || {}).flatMap(qr => qr.question_results || [])
          ).map((result, index) => {
            const song = quizData?.quiz_data?.[result.songIndex]?.song;
            const question = quizData?.quiz_data?.[result.songIndex]?.questions?.[result.questionIndex];

            return (
              <Accordion key={index} sx={{ mb: 2, borderRadius: '12px !important' }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: result.isCorrect ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                    borderRadius: '12px',
                    '&.Mui-expanded': {
                      borderRadius: '12px 12px 0 0',
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" width="100%">
                    <Box display="flex" alignItems="center" mr={2}>
                      {result.isTimeout ? (
                        <TimerIcon color="warning" />
                      ) : result.isCorrect ? (
                        <CheckIcon color="success" />
                      ) : (
                        <CancelIcon color="error" />
                      )}
                    </Box>

                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Question {index + 1}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {song?.name} • {song?.artist}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                      <Chip
                        label={`${result.timeTaken}s`}
                        size="small"
                        icon={<AccessTimeIcon />}
                        color="info"
                        variant="outlined"
                      />
                      <Chip
                        label={formatScore(result.scoreDelta || result.score || 0)}
                        size="small"
                        icon={<StarIcon />}
                        color={result.isCorrect ? 'success' : 'error'}
                        variant="filled"
                      />
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ backgroundColor: 'background.paper' }}>
                  <QuestionCard isCorrect={result.isCorrect}>
                    <CardContent>
                      {/* Question Text */}
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {question?.question_text || 'Question text not available'}
                      </Typography>

                      {/* Question Clue */}
                      {question?.question_clue && (
                        <Box mb={2} p={2} bgcolor="info.light" borderRadius={1}>
                          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'info.dark' }}>
                            💡 Hint: {question.question_clue}
                          </Typography>
                        </Box>
                      )}

                      {/* Answer Options */}
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                        Answer Options:
                      </Typography>

                      {question?.answers?.map((answer, answerIndex) => {
                        const isCorrect = answer.is_correct === 1;
                        const isSelected = result.userAnswer === answer.answer_id?.toString();

                        return (
                          <AnswerOption
                            key={answer.answer_id || answerIndex}
                            isCorrect={isCorrect}
                            isSelected={isSelected}
                          >
                            <Box display="flex" alignItems="center" mr={2}>
                              {isCorrect ? (
                                <CheckIcon color="success" />
                              ) : (
                                <CancelIcon color="error" />
                              )}
                            </Box>

                            <Box flex={1}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: isSelected ? 'bold' : 'normal',
                                  color: isCorrect ? 'success.main' : isSelected ? 'error.main' : 'text.primary'
                                }}
                              >
                                {answer.answer_text}
                              </Typography>

                              {answer.answer_reason && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isCorrect ? 'success.dark' : 'error.dark',
                                    mt: 0.5,
                                    display: 'block',
                                    fontStyle: 'italic'
                                  }}
                                >
                                  {isCorrect ? '✓' : '✗'} {answer.answer_reason}
                                </Typography>
                              )}
                            </Box>

                            {isSelected && (
                              <Chip
                                label="Your Answer"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </AnswerOption>
                        );
                      })}

                      {/* Question Stats */}
                      <Divider sx={{ my: 2 }} />

                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Box textAlign="center">
                            <Typography variant="caption" color="text.secondary">
                              Time Taken
                            </Typography>
                            <Typography variant="h6" color="info.main" fontWeight="bold">
                              {result.timeTaken}s
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box textAlign="center">
                            <Typography variant="caption" color="text.secondary">
                              Score Earned
                            </Typography>
                            <Typography
                              variant="h6"
                              color={result.isCorrect ? 'success.main' : 'error.main'}
                              fontWeight="bold"
                            >
                              {formatScore(result.scoreDelta || result.score || 0)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box textAlign="center">
                            <Typography variant="caption" color="text.secondary">
                              Result
                            </Typography>
                            <Chip
                              label={result.isTimeout ? 'Timeout' : result.isCorrect ? 'Correct' : 'Incorrect'}
                              color={result.isTimeout ? 'warning' : result.isCorrect ? 'success' : 'error'}
                              size="small"
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box textAlign="center">
                            <Typography variant="caption" color="text.secondary">
                              Difficulty
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {question?.level_name || 'Medium'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </QuestionCard>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </CardContent>
      </Card>

      {/* Enhanced Action Buttons */}
      <Card sx={{ borderRadius: '16px' }}>
        <CardContent sx={{ p: 4 }}>
          {/* <Typography variant="h6" textAlign="center" color="white" mb={3} fontWeight="bold">
            What would you like to do next?
          </Typography> */}
          <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
            {/* <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<ReplayIcon />}
                onClick={onReplay}
                size="large"
                sx={{
                  py: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #45a049 30%, #4CAF50 90%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Replay Quiz
              </Button>
            </Grid> */}
            {/* <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<ShareIcon />}
                onClick={handleShare}
                size="large"
                sx={{
                  py: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(45deg, #2196F3 30%, #1976D2 90%)',
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1976D2 30%, #2196F3 90%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Share Results
              </Button>
            </Grid> */}
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<HomeIcon />}
                onClick={onGoHome}
                size="large"
                sx={{
                  py: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(45deg, #FF9800 30%, #F57C00 90%)',
                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #F57C00 30%, #FF9800 90%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(255, 152, 0, 0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Go Home
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default QuizResults;
